<template>
    <FullModal ref="modal" :title="purchaseOrder?.id ? 'Purchase Order' : 'New Purchase Order'" :loading="isLoading">
        <div v-if="purchaseOrder" class="tw-flex tw-h-[min(calc(95vh_-_145px),760px)] tw-min-h-[560px]">
            <main class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                <div
                    v-if="purchaseOrder?.is_closed"
                    class="tw-text-center tw-bg-slate-200 tw-border-b tw-border-slate-400 tw-border-dashed tw-px-3 tw-py-2 tw-text-sm tw-text-slate-600"
                >
                    This purchase order has been marked as closed
                </div>
                <header class="tw-px-5 tw-py-4">
                    <div class="tw-flex tw-flex-wrap tw-items-start tw-gap-4">
                        <div class="tw-min-w-0 tw-flex-1">
                            <h2 class="tw-mt-0 tw-mb-1 tw-truncate tw-text-xl tw-text-slate-900">
                                <EditableText
                                    v-model="purchaseOrder.title"
                                    placeholder="Title"
                                    :max-length="255"
                                    @update:model-value="debouncedUpdatePO()"
                                />
                            </h2>
                            <div class="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
                                <ApprovalControls
                                    v-if="purchaseOrder.id && !isPooled"
                                    class="tw-mt-3"
                                    :disable-request="
                                        !purchaseOrder.line_items.length ||
                                        !lineItemsAreValid ||
                                        !!purchaseOrder.current_rms_opportunity_cost_id
                                    "
                                    :approvables="approvalPurchaseOrder"
                                    :custom-actions="approvalCustomActions"
                                    compact
                                    @reload="reloadCurrentPurchaseOrder"
                                ></ApprovalControls>
                                <template v-if="canClose && !isPooled">
                                    <Button v-if="!purchaseOrder.is_closed" compact @click="closePO()">
                                        <i class="mdi mdi-lock" />
                                        Close
                                    </Button>
                                    <Button v-else compact @click="reopenPO()">
                                        <i class="mdi mdi-lock-open" />
                                        Reopen
                                    </Button>
                                </template>
                                <Button v-if="canDelete" type="is-danger" compact @click="deletePO()">
                                    <i class="mdi mdi-delete" />
                                    Delete
                                </Button>
                            </div>
                        </div>
                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                            <pre
                                v-if="purchaseOrder.reference"
                                class="tw-m-0 tw-mr-1 tw-text-base tw-text-gray-700 tw-bg-gray-100 tw-px-2 tw-inline-block"
                                >{{ purchaseOrder.reference }}</pre
                            >
                            <Tooltip>
                                <template #content>
                                    <div class="tw-grid tw-grid-cols-2">
                                        <div class="tw-text-gray-400 tw-text-xs tw-border-r">Base</div>
                                        <div class="tw-text-gray-400 tw-text-xs tw-pl-1">Target</div>
                                        <div class="tw-border-b tw-border-r tw-pr-1">
                                            {{ purchaseOrder.base_currency }}
                                        </div>
                                        <div class="tw-border-b tw-pl-1">{{ purchaseOrder.target_currency }}</div>
                                        <div class="tw-col-span-2 tw-text-xs tw-border-t tw-pt-1">
                                            Rate: {{ purchaseOrder.conversion_rate }}
                                        </div>
                                    </div>
                                </template>
                                <Button
                                    v-if="purchaseOrder.status === 'Draft'"
                                    compact
                                    class="tw-text-gray-400 tw-font-normal tw-ml-1"
                                    @click="alterCurrency()"
                                >
                                    {{ purchaseOrder.target_currency }}
                                    <i class="mdi mdi-pencil tw-ml-1"></i>
                                </Button>
                            </Tooltip>
                            <Tag :status="purchaseOrder.status ?? 'Draft'" />
                            <Button v-if="canDownload" compact @click="downloadPDF()">
                                <i class="mdi mdi-download" />
                                Download
                            </Button>
                            <Button v-if="canEmail" type="is-info" compact @click="sendEmail()">
                                <i class="mdi mdi-email" />
                                Send
                            </Button>
                        </div>
                    </div>
                </header>

                <section class="tw-grid tw-grid-cols-1 tw-gap-0 lg:tw-grid-cols-10">
                    <dl class="tw-px-5 tw-pb-3 tw-m-0 tw-col-span-3">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Subcategory</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800">
                            <Selector
                                v-model="purchaseOrder.category_id"
                                :editable="canEdit"
                                :options="availableCategories"
                                :all-options="availableCategories"
                                :groups="availableCategoryGroups"
                                group-by="department_id"
                                @update:model-value="updateCategory()"
                            >
                                <template #optionLabel="{ option }">
                                    {{ option.name }}
                                </template>
                            </Selector>
                        </dd>
                    </dl>
                    <dl class="tw-px-5 tw-pb-3 tw-m-0 tw-col-span-3">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Supplier</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800">
                            <SupplierSelector
                                :model-value="purchaseOrder.supplier_id"
                                placeholder="Type to search"
                                :editable="canEdit"
                                @update:model-value="
                                    (val) => {
                                        updateSupplier(val ?? null);
                                    }
                                "
                            />
                        </dd>
                    </dl>
                    <dl class="tw-px-5 tw-pb-3 tw-m-0 tw-col-span-4">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Budget Entry</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800 tw-flex tw-gap-2 tw-items-center">
                            <Selector
                                v-model="purchaseOrder.budget_entry_id"
                                placeholder="Optional"
                                class="tw-flex-1"
                                :editable="canEdit"
                                :options="budgetEntries"
                                :all-options="budgetEntries"
                            />

                            <Tooltip v-if="purchaseOrder.budget_entry_id" label="View Budget Entry">
                                <Button @click="openLinkedEntry">
                                    <i class="mdi mdi-eye-outline"></i>
                                </Button>
                            </Tooltip>
                        </dd>
                    </dl>
                </section>

                <section class="tw-grid tw-grid-cols-1 tw-gap-px lg:tw-grid-cols-6 tw-select-none tw-mb-3 tw-border-y">
                    <dl class="tw-px-5 tw-py-3 tw-mb-0">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Subtotal</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800">
                            <Tooltip :disabled="!purchaseOrderIsMultiCurrency" class="tw-w-full">
                                <template #content>
                                    {{ formatCurrency(purchaseOrder.subtotal, 2, purchaseOrder.base_currency) }}
                                </template>
                                {{ formatCurrency(purchaseOrderSubtotalTarget, 2, purchaseOrder.target_currency) }}
                            </Tooltip>
                        </dd>
                    </dl>
                    <dl class="tw-px-5 tw-py-3 tw-m-0 tw-border-x">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Tax</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800">
                            <Tooltip :disabled="!purchaseOrderIsMultiCurrency" class="tw-w-full">
                                <template #content>
                                    {{ formatCurrency(purchaseOrder.tax_amount, 2, purchaseOrder.base_currency) }}
                                </template>
                                {{ formatCurrency(purchaseOrderTaxAmountTarget, 2, purchaseOrder.target_currency) }}
                            </Tooltip>
                        </dd>
                    </dl>
                    <dl class="tw-px-5 tw-py-3 tw-mb-0">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Total</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800">
                            <Tooltip :disabled="!purchaseOrderIsMultiCurrency" class="tw-w-full">
                                <template #content>
                                    {{ formatCurrency(purchaseOrder.total, 2, purchaseOrder.base_currency) }}
                                </template>
                                {{ formatCurrency(purchaseOrderTotalTarget, 2, purchaseOrder.target_currency) }}
                            </Tooltip>
                        </dd>
                    </dl>
                    <dl class="tw-px-5 tw-py-3 tw-m-0 tw-border-x">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Invoiced</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800">
                            {{ formatCurrency(purchaseOrderInvoicedTarget, 2, purchaseOrder?.target_currency) }}
                        </dd>
                    </dl>
                    <dl class="tw-px-5 tw-py-3 tw-m-0">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Paid</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800">
                            {{ formatCurrency(purchaseOrder?.total_paid_target, 2, purchaseOrder?.target_currency) }}
                        </dd>
                    </dl>
                    <dl class="tw-px-5 tw-py-3 tw-m-0 tw-border-l">
                        <dt class="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Outstanding</dt>
                        <dd class="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-800">
                            {{ formatCurrency(purchaseOrderOutstandingTarget, 2, purchaseOrder?.target_currency) }}
                        </dd>
                    </dl>
                </section>

                <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
                    <Tabs
                        v-model="activeTab"
                        :tabs="visibleTabs"
                        header-class="!tw-bg-transparent"
                        container-class="tw-bg-white tw-min-h-0 tw-flex-1 tw-overflow-hidden"
                        content-class="tw-h-full tw-overflow-y-auto"
                    >
                        <template #tab(line_items)>
                            <DataTable
                                :data="purchaseOrder.line_items"
                                :columns="[
                                    {
                                        field: 'drag_handle',
                                        width: 32,
                                        hidden: !canEdit || purchaseOrder.status !== 'Draft',
                                        headerClass: '!tw-border-t-0',
                                    },
                                    {
                                        title: 'Description',
                                        field: 'description',
                                        headerClass: '!tw-border-t-0' + (isApproved ? ' !tw-pl-4' : ''),
                                        class: isApproved ? ' !tw-pl-4' : '',
                                    },
                                    {
                                        title: 'Qty',
                                        field: 'quantity',
                                        headerClass: '!tw-border-t-0',
                                    },
                                    {
                                        title: 'Unit Price',
                                        field: 'unit_price_target',
                                        headerClass: '!tw-border-t-0',
                                    },
                                    {
                                        title: 'Discount',
                                        field: 'discount',
                                        headerClass: '!tw-border-t-0',
                                    },
                                    {
                                        title: 'Tax',
                                        field: 'tax_rate_id',
                                        headerClass: '!tw-border-t-0',
                                    },
                                    {
                                        title: 'Net Total',
                                        field: 'net_total',
                                        headerClass: '!tw-text-right !tw-border-t-0',
                                        class: '!tw-text-right',
                                    },
                                    {
                                        title: 'Gross Total',
                                        field: 'gross_total',
                                        headerClass: '!tw-text-right !tw-border-t-0',
                                        class: '!tw-text-right',
                                    },
                                    {
                                        field: 'actions',
                                        headerClass: '!tw-border-t-0',
                                        class: '!tw-text-right',
                                        width: 75,
                                        hidden: !canEdit || purchaseOrder.status !== 'Draft',
                                    },
                                ]"
                                :draggable="canEdit && purchaseOrder.status === 'Draft'"
                                @reorder="onLineItemsReordered"
                            >
                                <template #column(drag_handle)>
                                    <i
                                        v-if="canEdit && purchaseOrder.status === 'Draft'"
                                        class="drag-handle mdi mdi-drag-vertical tw-cursor-grab tw-text-gray-400 tw-text-lg"
                                    ></i>
                                </template>
                                <template #column(description)="{ index }">
                                    <Textarea
                                        v-if="canEdit && purchaseOrder.status === 'Draft'"
                                        v-model="purchaseOrder.line_items[index].description"
                                        :readonly="purchaseOrder.line_items[index].is_withholding_tax"
                                        maxlength="1000"
                                        placeholder="Enter a description"
                                        expandable
                                        @update:model-value="debouncedUpdatePO()"
                                    />
                                    <p
                                        v-else
                                        class="tw-whitespace-pre"
                                        v-html="purchaseOrder.line_items[index].description"
                                    ></p>
                                </template>
                                <template #column(quantity)="{ index }">
                                    <EditableText
                                        v-if="canEdit && purchaseOrder.status === 'Draft'"
                                        v-model="purchaseOrder.line_items[index].quantity"
                                        :editable="!purchaseOrder.line_items[index].is_withholding_tax"
                                        type="number"
                                        :min-value="0"
                                        :decimal-places="0"
                                        @update:model-value="debouncedUpdatePO()"
                                    />
                                </template>
                                <template #column(unit_price_target)="{ index }">
                                    <EditableText
                                        v-if="canEdit && purchaseOrder.status === 'Draft'"
                                        v-model="purchaseOrder.line_items[index].unit_price_target"
                                        :editable="!purchaseOrder.line_items[index].is_withholding_tax"
                                        type="currency"
                                        :min-value="0"
                                        :decimal-places="2"
                                        :currency-symbol="
                                            formatCurrency(0, 0, purchaseOrder.target_currency).replace('0', '')
                                        "
                                        @update:model-value="debouncedUpdatePO()"
                                    />
                                    <span v-else>
                                        {{
                                            formatCurrency(
                                                purchaseOrder.line_items[index].unit_price_target,
                                                2,
                                                purchaseOrder.target_currency
                                            )
                                        }}
                                    </span>
                                </template>
                                <template #column(discount)="{ row, index }">
                                    <div v-if="canEdit && purchaseOrder.status === 'Draft'" class="tw-flex">
                                        <Dropdown>
                                            <template #trigger>
                                                <Button
                                                    class="!tw-px-0 tw-w-9 tw-h-9"
                                                    :class="{
                                                        'tw-rounded-r-none tw-border-r-0':
                                                            purchaseOrder.line_items[index].discount_type !== 'none',
                                                    }"
                                                    @click="toggleLineItemDiscount(index)"
                                                >
                                                    <span
                                                        v-if="
                                                            purchaseOrder.line_items[index].discount_type === 'nominal'
                                                        "
                                                    >
                                                        {{
                                                            formatCurrency(0, 0, purchaseOrder.target_currency).replace(
                                                                '0',
                                                                ''
                                                            )
                                                        }}
                                                    </span>
                                                    <i
                                                        v-else-if="
                                                            purchaseOrder.line_items[index].discount_type ===
                                                            'percentage'
                                                        "
                                                        class="mdi mdi-percent"
                                                    ></i>
                                                    <i v-else class="mdi mdi-plus"></i>
                                                </Button>
                                            </template>
                                        </Dropdown>
                                        <EditableText
                                            v-if="purchaseOrder.line_items[index].discount_type !== 'none'"
                                            v-model="purchaseOrder.line_items[index].discount_value"
                                            class="tw-rounded-l-none"
                                            :editable="!purchaseOrder.line_items[index].is_withholding_tax"
                                            type="number"
                                            :min-value="0"
                                            :max-value="
                                                purchaseOrder.line_items[index].discount_type === 'percentage'
                                                    ? 100
                                                    : undefined
                                            "
                                            :decimal-places="2"
                                            @update:model-value="debouncedUpdatePO()"
                                        />
                                    </div>
                                    <span v-else>
                                        {{ discountLabel(row) }}
                                    </span>
                                </template>
                                <template #column(tax_rate_id)="{ row, index }">
                                    <TaxRateSelector
                                        v-if="canEdit && purchaseOrder.status === 'Draft'"
                                        v-model="purchaseOrder.line_items[index].tax_rate_id"
                                        type="expense"
                                        compact
                                        @update:model-value="debouncedUpdatePO()"
                                    />
                                    <span v-else>
                                        {{
                                            row.tax_rate_id
                                                ? localise(
                                                      useDataStore().get(`account.tax_rates.${row.tax_rate_id}.rate`),
                                                      2,
                                                      false
                                                  ) + '%'
                                                : 'None'
                                        }}
                                    </span>
                                </template>
                                <template #column(net_total)="{ row }">
                                    {{
                                        formatCurrency(
                                            subtotal(row, 'unit_price_target'),
                                            2,
                                            purchaseOrder.target_currency
                                        )
                                    }}
                                </template>
                                <template #column(gross_total)="{ row }">
                                    {{
                                        formatCurrency(
                                            total(row, 'unit_price_target'),
                                            2,
                                            purchaseOrder.target_currency
                                        )
                                    }}
                                </template>
                                <template #column(actions)="{ index }">
                                    <Button
                                        v-if="canEdit && purchaseOrder.status === 'Draft'"
                                        type="is-danger"
                                        class="!tw-px-2"
                                        @click="
                                            purchaseOrder.line_items.splice(index, 1);
                                            debouncedUpdatePO();
                                        "
                                    >
                                        <i class="mdi mdi-delete"></i>
                                    </Button>
                                </template>
                                <template #footer>
                                    <tfoot>
                                        <tr>
                                            <th :colspan="canEdit && purchaseOrder.status === 'Draft' ? 5 : 4">
                                                <Button
                                                    v-if="canEdit && purchaseOrder.status === 'Draft'"
                                                    @click="
                                                        purchaseOrder.line_items.push({
                                                            description: '',
                                                            quantity: 1,
                                                            unit_price: 0,
                                                            unit_price_target: 0,
                                                            discount_type: 'none',
                                                            discount_value: 0,
                                                            sort_order: purchaseOrder.line_items.length,
                                                            tax_rate_id: purchaseOrder.tax_rate_id
                                                                ? String(purchaseOrder.tax_rate_id)
                                                                : undefined,
                                                        })
                                                    "
                                                >
                                                    Add line item
                                                </Button>
                                            </th>
                                            <th>Totals</th>
                                            <td class="tw-text-right">
                                                {{
                                                    formatCurrency(
                                                        subtotal(purchaseOrder.line_items, 'unit_price_target'),
                                                        2,
                                                        purchaseOrder.target_currency
                                                    )
                                                }}
                                            </td>
                                            <td class="tw-text-right">
                                                {{
                                                    formatCurrency(
                                                        total(purchaseOrder.line_items, 'unit_price_target'),
                                                        2,
                                                        purchaseOrder.target_currency
                                                    )
                                                }}
                                            </td>
                                            <td v-if="canEdit && purchaseOrder.status === 'Draft'"></td>
                                        </tr>
                                        <tr>
                                            <td :colspan="canEdit && purchaseOrder.status === 'Draft' ? 9 : 7">
                                                <Checkbox
                                                    v-if="
                                                        useDataStore().get('account.xero_organisation') &&
                                                        useDataStore().get(
                                                            'account.xero_organisation.config.withholding_tax_amount'
                                                        )
                                                    "
                                                    v-model="withholdingTaxInfo.enable"
                                                    :disabled="
                                                        !!purchaseOrder.line_items.find(
                                                            (item) => item.is_withholding_tax
                                                        )
                                                    "
                                                    class="tw-w-fit"
                                                    @update:model-value="
                                                        (v) => {
                                                            if (v) {
                                                                addWithholdingTaxModal?.open();
                                                                return;
                                                            }
                                                        }
                                                    "
                                                >
                                                    Withholding Tax
                                                </Checkbox>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </template>
                            </DataTable>
                        </template>
                        <template #tab(budgeting)>
                            <div class="tw-flex tw-h-full tw-flex-col tw-gap-4 tw-p-5 tw-mb-10">
                                <div
                                    v-if="!isApproved"
                                    class="tw-rounded-md tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-800"
                                >
                                    This purchase order is not approved yet. These figures show the budget impact if it
                                    were approved now.
                                </div>

                                <div class="tw-grid tw-grid-cols-1 tw-gap-3 xl:tw-grid-cols-4">
                                    <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                                        <div class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">
                                            Budget
                                        </div>
                                        <div
                                            class="tw-mt-2 tw-text-right tw-text-xl tw-font-semibold tw-text-slate-900"
                                        >
                                            {{ formatCurrency(budgetEntryBudget, 2, purchaseOrder.target_currency) }}
                                        </div>
                                    </section>
                                    <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                                        <div class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">
                                            Committed elsewhere
                                        </div>
                                        <div
                                            class="tw-mt-2 tw-text-right tw-text-xl tw-font-semibold tw-text-slate-900"
                                        >
                                            {{
                                                formatCurrency(
                                                    budgetEntryCommittedElsewhere,
                                                    2,
                                                    purchaseOrder.target_currency
                                                )
                                            }}
                                        </div>
                                    </section>
                                    <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                                        <div class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">
                                            This PO
                                        </div>
                                        <div
                                            class="tw-mt-2 tw-text-right tw-text-xl tw-font-semibold tw-text-slate-900"
                                        >
                                            {{
                                                formatCurrency(purchaseOrderSubtotal, 2, purchaseOrder.target_currency)
                                            }}
                                        </div>
                                    </section>
                                    <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                                        <div class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">
                                            {{ budgetEntryRemaining < 0 ? 'Over budget' : 'Remaining' }}
                                        </div>
                                        <div
                                            class="tw-mt-2 tw-text-right tw-text-xl tw-font-semibold"
                                            :class="budgetEntryRemaining < 0 ? 'tw-text-red-600' : 'tw-text-slate-900'"
                                        >
                                            {{
                                                formatCurrency(
                                                    Math.abs(budgetEntryRemaining),
                                                    2,
                                                    purchaseOrder.target_currency
                                                )
                                            }}
                                        </div>
                                    </section>
                                </div>

                                <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white">
                                    <div
                                        class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-slate-200 tw-px-4 tw-pb-3"
                                    >
                                        <div>
                                            <h3 class="tw-mb-0 tw-text-base tw-text-slate-900">Linked budget entry</h3>
                                            <p class="tw-mb-0 tw-text-sm tw-text-slate-500">
                                                {{
                                                    purchaseOrder.budget_entry?.name ??
                                                    purchaseOrder.budget_entry?.title
                                                }}
                                            </p>
                                        </div>
                                        <Button
                                            v-if="purchaseOrder.budget_entry_id"
                                            type="is-link"
                                            compact
                                            @click="openLinkedEntry"
                                        >
                                            <i class="mdi mdi-open-in-new"></i>
                                            Open
                                        </Button>
                                    </div>
                                    <div class="tw-p-4">
                                        <div class="tw-flex tw-flex-col tw-gap-4">
                                            <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
                                                <div class="tw-min-w-0">
                                                    <div
                                                        class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400"
                                                    >
                                                        Budget entry
                                                    </div>
                                                    <div
                                                        class="tw-mt-1 tw-truncate tw-text-base tw-font-semibold tw-text-slate-900"
                                                    >
                                                        {{ purchaseOrder.budget_entry?.status ?? 'Linked' }}
                                                    </div>
                                                </div>
                                                <Button compact @click="openLinkedEntry">View</Button>
                                            </div>
                                            <div class="tw-grid tw-grid-cols-3 tw-gap-3 tw-text-sm">
                                                <div>
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">Budget</div>
                                                    <div class="tw-font-semibold tw-text-slate-900">
                                                        {{
                                                            formatCurrency(
                                                                budgetEntryImpact.budget,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">
                                                        Projected
                                                    </div>
                                                    <div class="tw-font-semibold tw-text-slate-900">
                                                        {{
                                                            formatCurrency(
                                                                budgetEntryImpact.projected,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <div class="tw-text-right">
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">
                                                        {{ budgetEntryImpact.overspend ? 'Over by' : 'Remaining' }}
                                                    </div>
                                                    <div
                                                        class="tw-font-semibold"
                                                        :class="
                                                            budgetEntryImpact.overspend
                                                                ? 'tw-text-red-600'
                                                                : 'tw-text-emerald-600'
                                                        "
                                                    >
                                                        {{
                                                            formatCurrency(
                                                                budgetEntryImpact.overspend ||
                                                                    budgetEntryImpact.remaining,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                class="tw-overflow-hidden tw-rounded-full tw-bg-slate-100 tw-ring-1 tw-ring-slate-200"
                                            >
                                                <div class="tw-flex tw-h-3">
                                                    <div
                                                        class="tw-bg-slate-400"
                                                        :style="{ width: `${budgetEntryImpact.committedWidth}%` }"
                                                    ></div>
                                                    <div
                                                        :class="
                                                            budgetEntryImpact.overspend
                                                                ? 'tw-bg-red-500'
                                                                : 'tw-bg-eventwise'
                                                        "
                                                        :style="{ width: `${budgetEntryImpact.impactWidth}%` }"
                                                    ></div>
                                                </div>
                                            </div>
                                            <div
                                                class="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-slate-500"
                                            >
                                                <span>
                                                    Committed elsewhere
                                                    {{
                                                        formatCurrency(
                                                            budgetEntryImpact.committed,
                                                            2,
                                                            purchaseOrder.target_currency
                                                        )
                                                    }}
                                                </span>
                                                <span>
                                                    This PO
                                                    {{
                                                        formatCurrency(
                                                            budgetEntryImpact.impact,
                                                            2,
                                                            purchaseOrder.target_currency
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <div
                                    v-if="budgetingFiguresLoaded"
                                    class="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-2"
                                >
                                    <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                                        <div class="tw-flex tw-flex-col tw-gap-4">
                                            <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
                                                <div class="tw-min-w-0">
                                                    <div
                                                        class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400"
                                                    >
                                                        Department
                                                    </div>
                                                    <div
                                                        class="tw-mt-1 tw-truncate tw-text-base tw-font-semibold tw-text-slate-900"
                                                    >
                                                        {{
                                                            budgetingFigures?.category?.department?.name ??
                                                            'Department budget'
                                                        }}
                                                    </div>
                                                </div>
                                                <Button compact @click="openBudgetPage('department')">View</Button>
                                            </div>
                                            <div class="tw-grid tw-grid-cols-3 tw-gap-3 tw-text-sm">
                                                <div>
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">Budget</div>
                                                    <div class="tw-font-semibold tw-text-slate-900">
                                                        {{
                                                            formatCurrency(
                                                                departmentImpact.budget,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">
                                                        Projected
                                                    </div>
                                                    <div class="tw-font-semibold tw-text-slate-900">
                                                        {{
                                                            formatCurrency(
                                                                departmentImpact.projected,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <div class="tw-text-right">
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">
                                                        {{ departmentImpact.overspend ? 'Over by' : 'Remaining' }}
                                                    </div>
                                                    <div
                                                        class="tw-font-semibold"
                                                        :class="
                                                            departmentImpact.overspend
                                                                ? 'tw-text-red-600'
                                                                : 'tw-text-emerald-600'
                                                        "
                                                    >
                                                        {{
                                                            formatCurrency(
                                                                departmentImpact.overspend ||
                                                                    departmentImpact.remaining,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                class="tw-overflow-hidden tw-rounded-full tw-bg-slate-100 tw-ring-1 tw-ring-slate-200"
                                            >
                                                <div class="tw-flex tw-h-3">
                                                    <div
                                                        class="tw-bg-slate-400"
                                                        :style="{ width: `${departmentImpact.committedWidth}%` }"
                                                    ></div>
                                                    <div
                                                        :class="
                                                            departmentImpact.overspend
                                                                ? 'tw-bg-red-500'
                                                                : 'tw-bg-eventwise'
                                                        "
                                                        :style="{ width: `${departmentImpact.impactWidth}%` }"
                                                    ></div>
                                                </div>
                                            </div>
                                            <div
                                                class="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-slate-500"
                                            >
                                                <span>
                                                    Committed elsewhere
                                                    {{
                                                        formatCurrency(
                                                            departmentImpact.committed,
                                                            2,
                                                            purchaseOrder.target_currency
                                                        )
                                                    }}
                                                </span>
                                                <span>
                                                    This PO
                                                    {{
                                                        formatCurrency(
                                                            departmentImpact.impact,
                                                            2,
                                                            purchaseOrder.target_currency
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </section>

                                    <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                                        <div class="tw-flex tw-flex-col tw-gap-4">
                                            <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
                                                <div class="tw-min-w-0">
                                                    <div
                                                        class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400"
                                                    >
                                                        Subcategory
                                                    </div>
                                                    <div
                                                        class="tw-mt-1 tw-truncate tw-text-base tw-font-semibold tw-text-slate-900"
                                                    >
                                                        {{ budgetingFigures?.category?.name ?? 'Subcategory budget' }}
                                                    </div>
                                                </div>
                                                <Button compact @click="openBudgetPage('category')">View</Button>
                                            </div>
                                            <div class="tw-grid tw-grid-cols-3 tw-gap-3 tw-text-sm">
                                                <div>
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">Budget</div>
                                                    <div class="tw-font-semibold tw-text-slate-900">
                                                        {{
                                                            formatCurrency(
                                                                categoryImpact.budget,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">
                                                        Projected
                                                    </div>
                                                    <div class="tw-font-semibold tw-text-slate-900">
                                                        {{
                                                            formatCurrency(
                                                                categoryImpact.projected,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <div class="tw-text-right">
                                                    <div class="tw-text-xs tw-uppercase tw-text-slate-400">
                                                        {{ categoryImpact.overspend ? 'Over by' : 'Remaining' }}
                                                    </div>
                                                    <div
                                                        class="tw-font-semibold"
                                                        :class="
                                                            categoryImpact.overspend
                                                                ? 'tw-text-red-600'
                                                                : 'tw-text-emerald-600'
                                                        "
                                                    >
                                                        {{
                                                            formatCurrency(
                                                                categoryImpact.overspend || categoryImpact.remaining,
                                                                2,
                                                                purchaseOrder.target_currency
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                class="tw-overflow-hidden tw-rounded-full tw-bg-slate-100 tw-ring-1 tw-ring-slate-200"
                                            >
                                                <div class="tw-flex tw-h-3">
                                                    <div
                                                        class="tw-bg-slate-400"
                                                        :style="{ width: `${categoryImpact.committedWidth}%` }"
                                                    ></div>
                                                    <div
                                                        :class="
                                                            categoryImpact.overspend
                                                                ? 'tw-bg-red-500'
                                                                : 'tw-bg-eventwise'
                                                        "
                                                        :style="{ width: `${categoryImpact.impactWidth}%` }"
                                                    ></div>
                                                </div>
                                            </div>
                                            <div
                                                class="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-slate-500"
                                            >
                                                <span>
                                                    Committed elsewhere
                                                    {{
                                                        formatCurrency(
                                                            categoryImpact.committed,
                                                            2,
                                                            purchaseOrder.target_currency
                                                        )
                                                    }}
                                                </span>
                                                <span>
                                                    This PO
                                                    {{
                                                        formatCurrency(
                                                            categoryImpact.impact,
                                                            2,
                                                            purchaseOrder.target_currency
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </section>
                                </div>

                                <div
                                    v-else
                                    class="tw-rounded-md tw-border tw-border-dashed tw-border-slate-300 tw-bg-slate-50 tw-p-8 tw-text-center tw-text-sm tw-text-slate-500"
                                >
                                    Loading wider budget figures...
                                </div>
                            </div>
                        </template>
                        <template #tab(invoices)>
                            <DataTable
                                :data="purchaseOrder.invoices ?? []"
                                :columns="[
                                    {
                                        title: 'Invoice #',
                                        field: 'invoice_number',
                                        class: 'tw-whitespace-nowrap !tw-pl-4',
                                        headerClass: '!tw-border-t-0 !tw-pl-4',
                                    },
                                    {
                                        title: 'Title',
                                        field: 'title',
                                        headerClass: '!tw-border-t-0',
                                    },
                                    {
                                        title: 'Subtotal',
                                        field: 'subtotal',
                                        headerClass: '!tw-text-right !tw-border-t-0',
                                        class: '!tw-text-right',
                                    },
                                    {
                                        title: 'Tax',
                                        field: 'tax_amount',
                                        headerClass: '!tw-text-right !tw-border-t-0',
                                        class: '!tw-text-right',
                                    },
                                    {
                                        title: 'Total',
                                        field: 'total_value',
                                        headerClass: '!tw-text-right !tw-border-t-0',
                                        class: '!tw-text-right',
                                    },
                                    {
                                        title: 'Outstanding',
                                        field: 'outstanding',
                                        headerClass: '!tw-text-right !tw-border-t-0',
                                        class: '!tw-text-right',
                                    },
                                    {
                                        title: '',
                                        field: 'meta',
                                        headerClass: '!tw-border-t-0',
                                    },
                                    {
                                        title: '',
                                        field: 'actions',
                                        class: '!tw-text-right',
                                        headerClass: '!tw-border-t-0',
                                    },
                                ]"
                                :row-expandable="invoiceHasAttachments"
                                :row-class="invoiceRowClass"
                            >
                                <template #column(invoice_number)="{ row }">
                                    {{ row.invoice_number }}
                                    <small class="tw-block tw-text-gray-400">
                                        {{ formatDate(row.invoice_date, 'dd/MM/yyyy') }}
                                    </small>
                                </template>
                                <template #column(title)="{ row }">
                                    {{ row.title }}
                                </template>
                                <template #column(subtotal)="{ row }">
                                    <Tooltip
                                        :label="formatCurrency(invoiceSubtotal(row, false), 2, row.base_currency)"
                                        :disabled="row.base_currency === row.target_currency"
                                        class="tw-w-full tw-text-right"
                                    >
                                        <span>
                                            {{ formatCurrency(invoiceSubtotal(row), 2, row.target_currency) }}
                                        </span>
                                    </Tooltip>
                                </template>
                                <template #column(tax_amount)="{ row }">
                                    <Tooltip
                                        :label="formatCurrency(invoiceTaxAmount(row, false), 2, row.base_currency)"
                                        :disabled="row.base_currency === row.target_currency"
                                        class="tw-w-full tw-text-right"
                                    >
                                        <span>
                                            {{ formatCurrency(invoiceTaxAmount(row), 2, row.target_currency) }}
                                        </span>
                                    </Tooltip>
                                </template>
                                <template #column(total_value)="{ row }">
                                    <Tooltip :disabled="!invoiceIsMultiCurrency(row)" class="tw-w-full tw-text-right">
                                        <template #content>
                                            {{ formatCurrency(invoiceTotalValue(row, false), 2, row.base_currency) }}
                                        </template>
                                        <span>
                                            {{ formatCurrency(invoiceTotalValue(row), 2, row.target_currency) }}
                                        </span>
                                        <div
                                            v-if="invoiceHasCredit(row)"
                                            class="tw-whitespace-nowrap tw-text-xs tw-text-gray-500"
                                        >
                                            (CR
                                            {{
                                                formatCurrency(
                                                    invoiceCreditValue(row),
                                                    2,
                                                    row.target_currency ? row.target_currency : row.base_currency
                                                )
                                            }})
                                        </div>
                                    </Tooltip>
                                </template>
                                <template #column(outstanding)="{ row }">
                                    <Tooltip :disabled="!invoiceIsMultiCurrency(row)" class="tw-w-full tw-text-right">
                                        <template #content>
                                            {{ formatCurrency(invoiceOutstanding(row, false), 2, row.base_currency) }}
                                        </template>
                                        <span>
                                            {{
                                                formatCurrency(
                                                    invoiceOutstanding(row),
                                                    2,
                                                    row.target_currency ? row.target_currency : row.base_currency
                                                )
                                            }}
                                        </span>
                                    </Tooltip>
                                </template>
                                <template #column(meta)="{ row }">
                                    <Tooltip class="tw-mx-auto" label="Attachments">
                                        <div
                                            class="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-gray-500"
                                        >
                                            <i class="mdi mdi-attachment tw-block -tw-rotate-45 tw-scale-125" />
                                            {{ row.file_uploads?.length }}
                                        </div>
                                    </Tooltip>
                                </template>
                                <template #column(actions)="{ row }">
                                    <Button @click="viewInvoice(row.id)">
                                        <i class="mdi mdi-eye"></i>
                                    </Button>
                                </template>
                                <template #expanded-row="{ row: expandedRow }">
                                    <DataTable
                                        v-if="expandedRow.file_uploads?.length"
                                        class="!tw-bg-slate-50"
                                        :columns="[
                                            {
                                                title: 'Attached Files',
                                                field: 'file_name',
                                            },
                                            {
                                                title: 'Size',
                                                field: 'file_size',
                                            },
                                            {
                                                title: '',
                                                field: 'actions',
                                                class: '!tw-text-right',
                                            },
                                        ]"
                                        :data="expandedRow.file_uploads"
                                    >
                                        <template #column(file_name)="{ row }">
                                            {{ row.file_name }}
                                        </template>
                                        <template #column(file_size)="{ row }">
                                            {{ bytes2mb(row.file_size) }}MB
                                        </template>
                                        <template #column(actions)="{ row }">
                                            <Button @click="downloadAttachment(row)">
                                                <i class="mdi mdi-download"></i>
                                            </Button>
                                        </template>
                                    </DataTable>
                                </template>
                            </DataTable>
                            <div v-if="hasMultiPurchaseOrderInvoices" class="tw-border-b tw-px-2 tw-pb-2">
                                <span class="tw-text-[.66rem] tw-font-medium tw-text-gray-400">Table Legend</span>
                                <div class="tw-flex tw-gap-2">
                                    <div
                                        class="tw-h-4 tw-w-4 tw-rounded tw-border tw-border-orange-200 tw-bg-orange-50"
                                    ></div>
                                    <span class="tw-text-xs tw-text-gray-400">
                                        Invoice is linked to multiple purchase orders
                                    </span>
                                </div>
                            </div>
                            <Button
                                v-if="!unableToRaiseInvoiceMessage"
                                type="is-link"
                                class="tw-flex tw-w-full tw-justify-center tw-gap-2 tw-py-2"
                                @click="raiseInvoice()"
                            >
                                <i class="mdi mdi-plus"></i>
                                Raise an invoice
                            </Button>
                            <p v-else class="empty-label tw-py-2" v-html="unableToRaiseInvoiceMessage"></p>
                        </template>
                        <template #tab(extra_info)>
                            <div class="tw-flex tw-h-full tw-flex-col tw-gap-4 tw-p-5 tw-pb-10">
                                <div class="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-2">
                                    <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white">
                                        <div
                                            class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-slate-200 tw-px-4 tw-py-3"
                                        >
                                            <div class="tw-min-w-0">
                                                <h3 class="tw-m-0 tw-text-base tw-text-slate-900">Currency</h3>
                                            </div>
                                            <Button
                                                v-if="canEdit && purchaseOrder.status === 'Draft'"
                                                compact
                                                @click="alterCurrency()"
                                            >
                                                <i class="mdi mdi-swap-horizontal"></i>
                                                Change
                                            </Button>
                                        </div>
                                        <dl
                                            class="tw-m-0 tw-grid tw-grid-cols-1 tw-gap-px tw-bg-slate-200 sm:tw-grid-cols-3"
                                        >
                                            <div class="tw-bg-white tw-p-4">
                                                <dt class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">
                                                    Base
                                                </dt>
                                                <dd
                                                    class="tw-mb-0 tw-mt-1 tw-text-base tw-font-semibold tw-text-slate-900"
                                                >
                                                    {{ purchaseOrder.base_currency }}
                                                </dd>
                                            </div>
                                            <div class="tw-bg-white tw-p-4">
                                                <dt class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">
                                                    Target
                                                </dt>
                                                <dd
                                                    class="tw-mb-0 tw-mt-1 tw-text-base tw-font-semibold tw-text-slate-900"
                                                >
                                                    {{ purchaseOrder.target_currency }}
                                                </dd>
                                            </div>
                                            <div class="tw-bg-white tw-p-4">
                                                <dt class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">
                                                    Rate
                                                </dt>
                                                <dd
                                                    class="tw-mb-0 tw-mt-1 tw-text-base tw-font-semibold tw-text-slate-900"
                                                >
                                                    {{ purchaseOrder.conversion_rate ?? 1 }}
                                                </dd>
                                            </div>
                                        </dl>
                                    </section>

                                    <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white">
                                        <div
                                            class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-slate-200 tw-px-4 tw-py-3"
                                        >
                                            <div class="tw-min-w-0">
                                                <h3 class="tw-m-0 tw-text-base tw-text-slate-900">Documents</h3>
                                            </div>
                                            <Button compact :disabled="!canEdit" @click="openAttachableDocuments()">
                                                <i class="mdi mdi-paperclip"></i>
                                                Manage
                                            </Button>
                                        </div>
                                        <div class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-4">
                                            <div>
                                                <div
                                                    class="tw-text-xl tw-font-semibold tw-leading-none tw-text-slate-900"
                                                >
                                                    {{ purchaseOrder.file_uploads?.length ?? 0 }}
                                                </div>
                                                <div class="tw-mt-1 tw-text-sm tw-text-slate-500">
                                                    document{{
                                                        (purchaseOrder.file_uploads?.length ?? 0) === 1 ? '' : 's'
                                                    }}
                                                    included
                                                </div>
                                            </div>
                                            <div
                                                class="tw-rounded-full tw-bg-slate-100 tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-text-slate-500"
                                            >
                                                <i class="mdi mdi-file-document-multiple-outline tw-text-xl"></i>
                                            </div>
                                        </div>
                                    </section>
                                </div>

                                <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white">
                                    <div class="tw-border-b tw-border-slate-200 tw-px-4 tw-py-3">
                                        <h3 class="tw-m-0 tw-text-base tw-text-slate-900">Delivery Address</h3>
                                    </div>
                                    <div class="tw-p-4">
                                        <Select
                                            v-model="purchaseOrder.delivery_address_id"
                                            :disabled="!canEdit"
                                            @update:model-value="debouncedUpdatePO()"
                                        >
                                            <option
                                                v-for="address of deliveryAddresses"
                                                :key="address.id"
                                                :disabled="address.is_archived"
                                                :value="address.id"
                                            >
                                                {{ address.address }}
                                            </option>
                                        </Select>
                                    </div>
                                </section>

                                <section class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white">
                                    <div class="tw-border-b tw-border-slate-200 tw-px-4 tw-py-3">
                                        <h3 class="tw-m-0 tw-text-base tw-text-slate-900">Additional information</h3>
                                        <p class="tw-m-0 tw-text-sm tw-text-slate-500">
                                            Payment notes or supplier-facing detail for this purchase order.
                                        </p>
                                    </div>
                                    <div class="tw-p-4">
                                        <Textarea
                                            v-model="purchaseOrder.custom_payment_details"
                                            :disabled="!canEdit"
                                            rows="5"
                                            placeholder="Add any extra details for the supplier..."
                                            @update:model-value="debouncedUpdatePO()"
                                        />
                                    </div>
                                </section>

                                <AttachDocuments ref="attachDocumentsModal" @updated="saveAttachableDocuments" />
                            </div>
                        </template>
                    </Tabs>
                    <AlterCurrency ref="alterCurrencyModal" @updated="updateCurrency" />
                </div>
            </main>

            <aside
                v-if="purchaseOrder.id"
                class="tw-flex tw-min-h-0 tw-flex-col tw-border-l tw-bg-slate-100 tw-transition-all tw-duration-200 tw-ease-out"
                :class="activityPanelOpen ? 'tw-w-[380px]' : 'tw-w-14'"
            >
                <div class="tw-flex tw-h-14 tw-items-center tw-gap-2 tw-border-b tw-bg-white tw-px-3">
                    <Transition name="activity-heading">
                        <div v-if="activityPanelOpen" class="tw-min-w-0">
                            <span class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Activity</span>
                        </div>
                    </Transition>
                    <Button
                        class="tw-ml-auto"
                        compact
                        square
                        :title="activityPanelOpen ? 'Collapse activity' : 'Expand activity'"
                        @click="activityPanelOpen = !activityPanelOpen"
                    >
                        <i
                            class="mdi"
                            :class="activityPanelOpen ? 'mdi-chevron-right' : 'mdi-comment-text-outline'"
                        ></i>
                    </Button>
                </div>

                <Transition name="activity-content">
                    <div v-if="activityPanelOpen" class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
                        <section class="tw-min-h-0 tw-flex-1">
                            <DrawerCommentsTab
                                :activity="purchaseOrder?.activity ?? []"
                                :comments="purchaseOrder?.comments ?? []"
                                :disabled="!purchaseOrder"
                                @comment="addComment"
                            />
                        </section>

                        <section
                            v-if="isConnectedToAcumatica"
                            class="tw-border-t tw-border-slate-200 tw-bg-white tw-px-4 tw-py-3"
                        >
                            <div class="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-3">
                                <span class="tw-text-xs tw-font-medium tw-uppercase tw-text-slate-400">Acumatica</span>
                            </div>

                            <a
                                v-if="purchaseOrder.acumatica_id"
                                :href="acumaticaPurchaseOrderUrl"
                                class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-md tw-bg-sky-100 tw-px-3 tw-py-2 tw-text-sm tw-text-sky-800 hover:tw-text-sky-900"
                                target="_blank"
                            >
                                <span>Purchase order pushed</span>
                                <i class="mdi mdi-open-in-new"></i>
                            </a>

                            <div
                                v-else-if="isApproved && purchaseOrder.integration_push_pending"
                                class="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-bg-sky-100 tw-px-3 tw-py-2 tw-text-sm tw-text-sky-800"
                            >
                                <i class="mdi mdi-autorenew mdi-spin"></i>
                                <span>Pushing to Acumatica...</span>
                            </div>

                            <Button
                                v-else-if="isApproved"
                                class="tw-w-full"
                                :disabled="isPushingToAcumatica"
                                :loading="isPushingToAcumatica"
                                compact
                                @click="pushToAcumatica"
                            >
                                Push to Acumatica
                            </Button>

                            <p v-else class="tw-m-0 tw-text-sm tw-text-slate-500">Available after approval.</p>
                        </section>
                    </div>
                </Transition>
            </aside>
        </div>
        <template v-if="purchaseOrder && !purchaseOrder.id" #footer>
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <Button @click="close()">Cancel</Button>
                <div class="tw-flex tw-items-center tw-gap-2">
                    <Button
                        :disabled="!purchaseOrderIsReadyToCreate"
                        :loading="isUpdating"
                        @click="createPurchaseOrder()"
                    >
                        Create
                    </Button>
                    <Button
                        type="is-primary"
                        :disabled="!purchaseOrderIsReadyToCreate"
                        :loading="isUpdating"
                        @click="createPurchaseOrder({ viewAfterCreate: true })"
                    >
                        Create & View
                    </Button>
                </div>
            </div>
        </template>
    </FullModal>
    <Modal ref="addWithholdingTaxModal" title="Add Withholding Tax" @closed="withholdingTaxInfo.enable = false">
        <template v-if="purchaseOrder">
            <FormField label="Withholding Tax Percentage">
                <PercentageValue
                    v-model="withholdingTaxInfo.percentage"
                    class="tw-w-fit"
                    name="withholding_tax_amount"
                    :force-percentage="true"
                    placeholder="20%"
                />
            </FormField>
            <FormField label="Amount" class="tw-mt-3">
                <Currency
                    v-model="withholdingTaxInfo.amount"
                    :currency="formatCurrency(0, 0, purchaseOrder.target_currency).replace('0', '')"
                />
            </FormField>
            <Button
                class="tw-mt-3 tw-float-right"
                type="is-primary"
                :disabled="!withholdingTaxInfo.amount"
                @click="addWithholdingTaxLineItem"
            >
                Add
            </Button>
        </template>
    </Modal>
</template>
<script setup lang="ts">
import FullModal from '@/js/components/FullModal.vue';
import Modal from '@/js/components/Modal.vue';
import Button from '@/js/components/controls/Button.vue';
import EditableText from '@/js/components/controls/EditableText.vue';
import Tabs from '@/js/components/widgets/Tabs.vue';
import DrawerCommentsTab from '@/assets/js/eventbooks/components/drawer-tabs/comments.vue';
import SupplierSelector from '@/js/components/controls/SupplierSelector.vue';
import Selector from '@/js/components/controls/Selector.vue';
import Select from '@/js/components/controls/Select.vue';
import { Eventbus } from '@/js/utils/eventbus.ts';
import { axios } from '@/js/utils/axios.ts';
import { totalOutstanding as invoiceOutstanding } from '@/js/utils/invoiceTotals.js';
import { Auth, formatCurrency, formatDate, localise, route, toast } from '@/js/utils.js';
import { budgetRouteNameForPreference } from '@/js/utils/budgetRoutes.ts';
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, useTemplateRef, watch } from 'vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import { subtotal, total, withholdingTaxAmount } from '@/js/composables/useLineItemTotals.js';
import { debounce } from 'lodash';
import Textarea from '@/js/components/controls/Textarea.vue';
import TaxRateSelector from '@/js/components/controls/TaxRateSelector.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import DataTable from '@/js/components/tables/DataTable.vue';
import ApprovalControls from '@/js/components/widgets/ApprovalControls.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import Tag from '@/js/components/Tag.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import PercentageValue from '@/js/components/widgets/PercentageValue.vue';
import Currency from '@/js/components/controls/Currency.vue';
import { confirm } from '@/js/composables/useModals.ts';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import AlterCurrency from '@/js/components/modals/AlterCurrency.vue';
import AttachDocuments from '@/js/components/modals/AttachDocuments.vue';
import Dropdown from '@/js/components/controls/Dropdown.vue';
import type { Approvable } from '@/js/types/approvable.ts';
import type { Address as DeliveryAddress } from '@/js/types/address.ts';
import type { BudgetingFigures } from '@/js/types/budgetingFigures.ts';
import type { Category } from '@/js/types/category.ts';
import type { Department } from '@/js/types/department.ts';
import type { FileUpload } from '@/js/types/fileUpload.ts';
import type { PurchaseOrder as PurchaseOrderModel } from '@/js/types/purchaseOrder.ts';
import type { PurchaseOrderLineItem as LineItem } from '@/js/types/purchaseOrderLineItem.ts';
import type { SupplierInvoice } from '@/js/types/supplierInvoice.ts';

type Tab = {
    title: string;
    name: string;
    headerClass?: string;
};

type CreatePurchaseOrderPayload = {
    department?: number;
    category?: number;
    title?: string;
    supplier?: number;
    entry?: number;
};

type CreatePurchaseOrderEventPayload = CreatePurchaseOrderPayload | void;

type CreatePurchaseOrderOptions = {
    viewAfterCreate?: boolean;
};

type ApprovalAction = {
    label: string;
    payload?: Record<string, unknown>;
    disabled?: boolean;
};

type EditablePurchaseOrder = Partial<PurchaseOrderModel> & {
    title: string;
    status: string;
    target_currency: string;
    base_currency: string;
    conversion_rate: number | string;
    line_items: LineItem[];
};

type CategoryGroup = Pick<Department, 'id' | 'name'>;

const modal = useTemplateRef('modal');
const alterCurrencyModal = useTemplateRef('alterCurrencyModal');
const attachDocumentsModal = useTemplateRef('attachDocumentsModal');
const addWithholdingTaxModal = useTemplateRef('addWithholdingTaxModal');
const emitUntypedEvent = Eventbus.$emit as (name: string, data?: unknown) => void;
const createBlankPurchaseOrder = (): EditablePurchaseOrder => {
    const accountCurrencyCode = useDataStore().get('account.currency_code', 'GBP');
    const currencyCode = useDataStore().get('current_event.currency_code', accountCurrencyCode);
    const defaultCurrencyCode = typeof currencyCode === 'string' && currencyCode ? currencyCode : 'GBP';

    return {
        title: '',
        status: 'Draft',
        supplier_id: null,
        category_id: undefined,
        budget_entry_id: undefined,
        tax_rate_id: null,
        base_currency: defaultCurrencyCode,
        target_currency: defaultCurrencyCode,
        conversion_rate: 1,
        subtotal: 0,
        total: 0,
        tax_amount: 0,
        total_paid_target: 0,
        total_invoiced: 0,
        total_outstanding: 0,
        is_closed: false,
        line_items: [
            {
                description: '',
                quantity: 1,
                unit_price: 0,
                unit_price_target: 0,
                discount_type: 'none',
                discount_value: 0,
                sort_order: 0,
                tax_rate_id: undefined,
            },
        ],
        invoices: [],
        activity: [],
        comments: [],
    };
};

const purchaseOrder = ref<EditablePurchaseOrder | null>(createBlankPurchaseOrder());
const initialSupplierId = ref<number | null>(null);
const withholdingTaxInfo = reactive({
    enable: false,
    percentage: useDataStore().get('account.xero_organisation')?.config?.withholding_tax_amount ?? 0,
    amount: 0,
});

watch(
    [() => withholdingTaxInfo.percentage, () => purchaseOrder.value?.line_items],
    () => {
        withholdingTaxInfo.amount = withholdingTaxAmount(
            purchaseOrder.value?.line_items ?? [],
            withholdingTaxInfo.percentage,
            'unit_price_target'
        );
    },
    { deep: true, immediate: true }
);
const budgetEntries = ref([]);
const loadedEventCategories = ref<Category[] | null>(null);
const isLoading = ref(false);
const isUpdating = ref(false);
const isPushingToAcumatica = ref(false);
const isHydratingPurchaseOrder = ref(false);
const activityPanelOpen = ref(true);
const activeTab = ref('line_items');
const budgetingFigures = ref<BudgetingFigures | null>(null);
const budgetingFiguresLoaded = ref(false);

const toNumber = (value: number | string | null | undefined): number => {
    const parsedValue = Number(value ?? 0);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const hasValue = (value: number | string | null | undefined): boolean => {
    return value !== null && value !== undefined && value !== '';
};

const valueOrFallback = (
    value: number | string | null | undefined,
    fallback: number | string | null | undefined
): number | string | null | undefined => {
    return hasValue(value) ? value : fallback;
};

const normaliseLineItemDiscount = (lineItem: LineItem): void => {
    lineItem.discount_type = lineItem.discount_type ?? 'none';

    if (lineItem.discount_type === 'none') {
        lineItem.discount_value = 0;
    }
};

const discountLabel = (lineItem: LineItem): string => {
    if (!lineItem.discount_type || lineItem.discount_type === 'none' || !toNumber(lineItem.discount_value)) {
        return 'None';
    }

    if (lineItem.discount_type === 'percentage') {
        return `${localise(lineItem.discount_value, 2, false)}%`;
    }

    return formatCurrency(lineItem.discount_value, 2, purchaseOrder.value?.target_currency);
};

const visibleTabs = computed<Tab[]>(() => {
    const tabs: Tab[] = [
        {
            title: 'Line Items',
            name: 'line_items',
            headerClass: 'tw-ml-2',
        },
    ];

    if (purchaseOrder.value?.id && purchaseOrder.value?.budget_entry_id) {
        tabs.push({
            title: 'Budgeting',
            name: 'budgeting',
        });
    }
    if (
        purchaseOrder.value?.id &&
        (isApproved.value || (purchaseOrder.value?.invoices && purchaseOrder.value.invoices.length))
    ) {
        tabs.push({
            title: 'Invoices',
            name: 'invoices',
        });
    }
    if (purchaseOrder.value && !isPooled.value) {
        tabs.push({
            title: 'Extra Info',
            name: 'extra_info',
        });
    }
    return tabs;
});

const unableToRaiseInvoiceMessage = computed(() => {
    if (!isApproved.value || purchaseOrder.value?.is_closed) {
        return purchaseOrder.value?.is_closed
            ? 'You cannot raise a new invoice on a closed PO'
            : 'You can only raise invoices on an approved PO';
    }

    if (!canEdit.value && !purchaseOrder.value?.current_rms_opportunity_cost_id) {
        return 'This purchase order is no longer editable.';
    }

    if (!Auth.can('create supplier invoices')) {
        return 'You do not have permission to raise invoices - please contact your account owner to obtain permission.';
    }

    return null;
});

const hasMultiPurchaseOrderInvoices = computed(() => {
    return !!purchaseOrder.value?.invoices?.filter((invoice) => isMultiPurchaseOrderInvoice(invoice)).length;
});

const approvalPurchaseOrder = computed<Approvable[]>(() => {
    if (!purchaseOrder.value?.id || !purchaseOrder.value.event_id) {
        return [];
    }

    return [purchaseOrder.value as unknown as Approvable];
});

const approvalCustomActions = computed<Partial<Record<'approve', ApprovalAction[]>>>(() => {
    if (!isConnectedToAcumatica.value) {
        return {};
    }

    return {
        approve: [
            {
                label: 'Approve without pushing',
                payload: {
                    options: {
                        skip_integration_push: true,
                    },
                },
            },
        ],
    };
});

const deliveryAddresses = computed<DeliveryAddress[]>(() => {
    const accountAddresses = Object.values(useDataStore().get('account.addresses') ?? {}) as DeliveryAddress[];

    return accountAddresses
        .filter((address) => address.type !== 'account')
        .filter((address) => !address.is_archived || address.id === purchaseOrder.value?.delivery_address_id);
});

const availableCategories = computed<Category[]>(() => {
    const categories =
        loadedEventCategories.value ??
        (Object.values(useDataStore().get('current_event.categories', {})) as Category[]);

    return categories.filter((category) => !category.is_revenue);
});

const availableCategoryGroups = computed<CategoryGroup[]>(() => {
    if (!loadedEventCategories.value) {
        return Object.values(useDataStore().get('current_event.cost_departments', {})) as CategoryGroup[];
    }

    return Array.from(
        new Map(
            loadedEventCategories.value
                .filter((category) => category.department)
                .map((category) => [category.department!.id, category.department!] as const)
        ).values()
    );
});

const isPooled = computed(() => {
    return purchaseOrder.value?.is_pooled ?? false;
});
const isApproved = computed(() => {
    return ['Approved', 'Sent', 'Complete'].includes(purchaseOrder.value?.status ?? '');
});
const isConnectedToAcumatica = computed(() => {
    return (
        useDataStore().get('account.is_connected_to_acumatica') && useDataStore().get('account.integrations.acumatica')
    );
});
const acumaticaPurchaseOrderUrl = computed(() => {
    if (!isConnectedToAcumatica.value || !purchaseOrder.value?.reference) {
        return undefined;
    }

    const acumaticaIntegration = useDataStore().get('account.integrations.acumatica') as
        | {
              oauth_url?: string;
              organisation_name?: string;
          }
        | undefined;

    if (!acumaticaIntegration?.oauth_url || !acumaticaIntegration.organisation_name) {
        return undefined;
    }

    const companyId = acumaticaIntegration.organisation_name.replaceAll(' ', '+');

    return `${acumaticaIntegration.oauth_url}/Main?CompanyID=${companyId}&ScreenId=PO301000&OrderType=RO&OrderNbr=${purchaseOrder.value.reference}`;
});
const canEdit = computed(() => {
    // Don't allow editing if this is from Current RMS
    if (purchaseOrder.value && purchaseOrder.value.current_rms_opportunity_cost_id) {
        return false;
    }
    return Auth.can('update purchase orders') && !useDataStore().get('current_event.is_closed');
});
const canClose = computed(() => {
    return Auth.can('update purchase orders') && ['Approved', 'Sent'].includes(purchaseOrder.value?.status ?? '');
});
const canDelete = computed(() => {
    return !!purchaseOrder.value?.id && Auth.can('delete purchase orders') && canEdit.value;
});
const canDownload = computed(() => {
    return (
        !!purchaseOrder.value?.id &&
        (['Approved', 'Sent'].includes(purchaseOrder.value?.status ?? '') ||
            (['Awaiting Approval'].includes(purchaseOrder.value?.status ?? '') &&
                useDataStore().get('account.features.draft_po_pdf') &&
                useDataStore().get('account.preferences.enable_po_download')))
    );
});
const canEmail = computed(() => {
    return (
        ['Approved', 'Sent'].includes(purchaseOrder.value?.status ?? '') &&
        canEdit.value &&
        useDataStore().get('account.preferences.enable_po_email')
    );
});

watch(visibleTabs, (tabs) => {
    if (!tabs.some((tab) => tab.name === activeTab.value)) {
        activeTab.value = tabs[0]?.name ?? 'line_items';
    }
});

const purchaseOrderSubtotal = computed(() => {
    return toNumber(purchaseOrder.value?.subtotal);
});

const purchaseOrderIsMultiCurrency = computed(() => {
    return (
        !!purchaseOrder.value?.base_currency &&
        !!purchaseOrder.value?.target_currency &&
        purchaseOrder.value.base_currency !== purchaseOrder.value.target_currency
    );
});

const purchaseOrderSubtotalTarget = computed(() => {
    return valueOrFallback(
        purchaseOrder.value?.subtotal_target,
        purchaseOrder.value?.id
            ? purchaseOrder.value?.subtotal
            : subtotal(purchaseOrder.value?.line_items ?? [], 'unit_price_target')
    );
});

const purchaseOrderTaxAmountTarget = computed(() => {
    return valueOrFallback(
        purchaseOrder.value?.tax_amount_target,
        purchaseOrder.value?.id
            ? purchaseOrder.value?.tax_amount
            : total(purchaseOrder.value?.line_items ?? [], 'unit_price_target') -
                  subtotal(purchaseOrder.value?.line_items ?? [], 'unit_price_target')
    );
});

const purchaseOrderTotalTarget = computed(() => {
    return valueOrFallback(
        purchaseOrder.value?.total_target,
        purchaseOrder.value?.id
            ? purchaseOrder.value?.total
            : total(purchaseOrder.value?.line_items ?? [], 'unit_price_target')
    );
});

const purchaseOrderInvoicedTarget = computed(() => {
    return valueOrFallback(purchaseOrder.value?.total_invoiced_gross_target, purchaseOrder.value?.total_invoiced);
});

const purchaseOrderOutstandingTarget = computed(() => {
    if (purchaseOrderIsMultiCurrency.value && hasValue(purchaseOrder.value?.total_invoiced_gross_target)) {
        return Math.max(
            toNumber(purchaseOrder.value?.total_invoiced_gross_target) -
                toNumber(purchaseOrder.value?.total_paid_target),
            0
        );
    }

    if (hasValue(purchaseOrder.value?.total_outstanding)) {
        return purchaseOrder.value?.total_outstanding;
    }

    if (hasValue(purchaseOrder.value?.outstanding)) {
        return purchaseOrder.value?.outstanding;
    }

    return Math.max(toNumber(purchaseOrderInvoicedTarget.value) - toNumber(purchaseOrder.value?.total_paid_target), 0);
});

const approvedBudgetImpact = computed(() => {
    return isApproved.value ? 0 : purchaseOrderSubtotal.value;
});

const budgetEntryBudget = computed(() => {
    return toNumber(purchaseOrder.value?.budget_entry?.budgeted_cost);
});

const budgetEntryCommittedElsewhere = computed(() => {
    const committed = toNumber(purchaseOrder.value?.budget_entry?.total_purchaseorders);

    if (!isApproved.value) {
        return committed;
    }

    return Math.max(committed - purchaseOrderSubtotal.value, 0);
});

const budgetEntryRemaining = computed(() => {
    return budgetEntryBudget.value - budgetEntryCommittedElsewhere.value - purchaseOrderSubtotal.value;
});

const departmentBudget = computed(() => {
    return toNumber(budgetingFigures.value?.departmentFigures?.figures?.budget);
});

const departmentCommitted = computed(() => {
    return toNumber(budgetingFigures.value?.departmentFigures?.figures?.committed);
});

const categoryBudget = computed(() => {
    return toNumber(budgetingFigures.value?.categoryFigures?.figures?.budget);
});

const categoryCommitted = computed(() => {
    return toNumber(budgetingFigures.value?.categoryFigures?.figures?.committed);
});

const createBudgetImpact = (budget: number, committed: number, impact: number) => {
    const projected = committed + impact;
    const remaining = budget - projected;
    const max = Math.max(budget, projected, 1);
    const committedWidth = Math.min((committed / max) * 100, 100);
    const impactWidth = Math.min((impact / max) * 100, 100 - committedWidth);
    const overspend = Math.max(projected - budget, 0);

    return {
        budget,
        committed,
        impact,
        projected,
        remaining,
        committedWidth,
        impactWidth,
        overspend,
    };
};

const budgetEntryImpact = computed(() => {
    return createBudgetImpact(
        budgetEntryBudget.value,
        budgetEntryCommittedElsewhere.value,
        purchaseOrderSubtotal.value
    );
});

const departmentImpact = computed(() => {
    return createBudgetImpact(departmentBudget.value, departmentCommitted.value, approvedBudgetImpact.value);
});

const categoryImpact = computed(() => {
    return createBudgetImpact(categoryBudget.value, categoryCommitted.value, approvedBudgetImpact.value);
});

const lineItemsAreValid = computed(() => {
    return !purchaseOrder.value?.line_items.find(
        (lineItem) => !lineItem.quantity || !hasValue(lineItem.unit_price_target) || !lineItem.description
    );
});

const purchaseOrderIsReadyToCreate = computed(() => {
    return (
        !!purchaseOrder.value?.category_id &&
        !!purchaseOrder.value?.supplier_id &&
        !!purchaseOrder.value?.title &&
        !!purchaseOrder.value?.target_currency &&
        !!purchaseOrder.value?.conversion_rate &&
        !!purchaseOrder.value?.line_items?.filter(
            (lineItem) => lineItem.description && lineItem.quantity && hasValue(lineItem.unit_price_target)
        ).length
    );
});

function open(): void {
    debouncedUpdatePO.cancel();
    purchaseOrder.value = createBlankPurchaseOrder();
    initialSupplierId.value = null;
    activeTab.value = 'line_items';
    budgetEntries.value = [];
    loadedEventCategories.value = null;
    budgetingFigures.value = null;
    budgetingFiguresLoaded.value = false;
    modal.value?.open();
}

function close(): void {
    debouncedUpdatePO.cancel();
    modal.value?.close();
}

async function loadPurchaseOrder(purchaseOrderId: number): Promise<void> {
    isLoading.value = true;
    isHydratingPurchaseOrder.value = true;
    debouncedUpdatePO.cancel();

    try {
        const { data } = await axios.get(
            route('api.account.purchase-orders.get', {
                purchaseOrder: purchaseOrderId,
            })
        );
        const loadedPurchaseOrder = data as EditablePurchaseOrder;
        loadedPurchaseOrder.line_items.forEach(normaliseLineItemDiscount);
        isPushingToAcumatica.value = false;
        purchaseOrder.value = loadedPurchaseOrder;
        initialSupplierId.value = loadedPurchaseOrder.supplier_id ?? null;
        await loadAvailableCategories();
        await loadBudgetEntries();
        await loadBudgetingFigures();
        await nextTick();
        debouncedUpdatePO.cancel();
    } finally {
        isHydratingPurchaseOrder.value = false;
        isLoading.value = false;
    }
}

const reloadCurrentPurchaseOrder = async (): Promise<void> => {
    if (!purchaseOrder.value?.id) {
        return;
    }

    await loadPurchaseOrder(purchaseOrder.value.id);
};

const getDepartmentDefaultTaxRate = (departmentId?: number): number | null => {
    if (!departmentId) {
        return null;
    }

    const department = useDataStore().get(`account.departments.${departmentId}`) as Department | null;
    const taxRateId = department?.default_tax_rate_id;

    return taxRateId ? Number(taxRateId) : null;
};

const getCategoryDepartmentId = (categoryId?: number): number | undefined => {
    if (!categoryId) {
        return undefined;
    }

    const category = availableCategories.value.find((availableCategory) => availableCategory.id === categoryId);

    return category?.department_id;
};

const loadAvailableCategories = async (): Promise<void> => {
    loadedEventCategories.value = null;

    const eventId = purchaseOrder.value?.event_id ?? route().params.event;
    const currentEventId = useDataStore().get('current_event.id', null);

    if (!eventId || Number(currentEventId) === Number(eventId)) {
        return;
    }

    const { data } = await axios.get(
        route('api.account.event.categories', {
            account: purchaseOrder.value?.account_id,
            event: eventId,
            is_revenue: 0,
        })
    );

    loadedEventCategories.value = data as Category[];
};

const applyDefaultTaxRate = (departmentId?: number): void => {
    if (!purchaseOrder.value) {
        return;
    }

    const defaultTaxRateId = getDepartmentDefaultTaxRate(departmentId);

    if (!defaultTaxRateId) {
        return;
    }

    purchaseOrder.value.tax_rate_id = defaultTaxRateId;
    purchaseOrder.value.line_items.forEach((lineItem) => {
        lineItem.tax_rate_id = String(defaultTaxRateId);
    });
};

const loadBudgetEntries = async () => {
    const eventId = purchaseOrder.value?.event_id ?? route().params.event;

    if (!eventId) {
        return;
    }

    if (!purchaseOrder.value?.category_id) {
        budgetEntries.value = [];
        return;
    }

    const { data } = await axios.get(
        route('api.account.event.entries', {
            event: eventId,
            category_id: purchaseOrder.value.category_id,
        })
    );
    budgetEntries.value = data;
};

const populateLineItemFromBudgetEntry = async (): Promise<void> => {
    if (!purchaseOrder.value?.budget_entry_id) {
        return;
    }

    const { data } = await axios.get(
        route('api.account.event.entries.get', {
            entry: purchaseOrder.value.budget_entry_id,
        })
    );

    const committed = (data.purchase_orders ?? []).reduce((totalValue: number, order: PurchaseOrderModel) => {
        return totalValue + toNumber(order.subtotal);
    }, 0);
    const uncommittedBudget = toNumber(data?.budgeted_cost) - committed;

    purchaseOrder.value.line_items = [
        {
            description: purchaseOrder.value.title,
            quantity: 1,
            unit_price: uncommittedBudget,
            unit_price_target: uncommittedBudget,
            sort_order: 0,
            tax_rate_id: purchaseOrder.value.tax_rate_id ? String(purchaseOrder.value.tax_rate_id) : undefined,
        },
    ];
};

const loadBudgetingFigures = async () => {
    budgetingFiguresLoaded.value = false;
    budgetingFigures.value = null;

    if (!purchaseOrder.value?.account_id || !purchaseOrder.value?.event_id || !purchaseOrder.value?.category_id) {
        return;
    }

    const { data } = await axios.get(
        route('api.account.event.purchaseorders.budgetingfigures', {
            account: purchaseOrder.value.account_id,
            event: purchaseOrder.value.event_id,
            category: purchaseOrder.value.category_id,
        })
    );

    budgetingFigures.value = data;
    budgetingFiguresLoaded.value = true;
};

const openBudgetPage = (scope: 'department' | 'category') => {
    if (!purchaseOrder.value?.event_id || !budgetingFigures.value?.category?.department?.id) {
        return;
    }

    window.location.href = route(
        budgetRouteNameForPreference(
            useDataStore().get('account.preferences.default_budget_view'),
            useDataStore().get('account.features.agency_budget')
        ),
        {
            event: purchaseOrder.value.event_id,
            page: 'costs',
            departmentId: budgetingFigures.value.category.department.id,
            categoryId: scope === 'category' ? budgetingFigures.value.category.id : undefined,
        }
    );
};

const debouncedUpdatePO = debounce(() => {
    if (isLoading.value || isHydratingPurchaseOrder.value) {
        return;
    }

    updatePO();
}, 1500);

const createPurchaseOrder = async (options: CreatePurchaseOrderOptions = {}) => {
    if (!purchaseOrder.value || !purchaseOrderIsReadyToCreate.value || isLoading.value || isUpdating.value) {
        return;
    }

    isUpdating.value = true;
    try {
        purchaseOrder.value.tax_rate_id = purchaseOrder.value.tax_rate_id ?? null;
        const { data } = await axios.post(route('api.account.event.purchaseorders.create'), purchaseOrder.value);
        capturePosthogEvent('purchase_order_created');
        Eventbus.$emit('reload:purchaseorders');
        emitUntypedEvent('reload:statistics');
        toast('Success', 'Purchase Order Created', 'success');

        if (options.viewAfterCreate) {
            await loadPurchaseOrder(data.id);
            return;
        }

        close();
    } finally {
        isUpdating.value = false;
    }
};

const updatePO = async () => {
    if (!purchaseOrder.value || isLoading.value || isHydratingPurchaseOrder.value) {
        return;
    }

    if (!purchaseOrder.value.id) {
        return;
    }

    if (!purchaseOrder.value.supplier_id) {
        return;
    }

    isUpdating.value = true;
    try {
        await axios.patch(
            route('api.account.event.purchaseorders.update', {
                account: purchaseOrder.value.account_id,
                event: purchaseOrder.value.event_id,
                purchaseOrder: purchaseOrder.value.id,
            }),
            purchaseOrder.value
        );
        capturePosthogEvent('purchase_order_updated');
        await loadPurchaseOrder(purchaseOrder.value.id);
        Eventbus.$emit('reload:purchaseorders');
        emitUntypedEvent('reload:statistics');
    } finally {
        isUpdating.value = false;
    }
};

const updateCategory = async () => {
    if (isLoading.value || isHydratingPurchaseOrder.value) {
        return;
    }

    applyDefaultTaxRate(getCategoryDepartmentId(purchaseOrder.value?.category_id));
    await loadBudgetEntries();
    debouncedUpdatePO();
};

const openForCreation = async (payload?: CreatePurchaseOrderPayload): Promise<void> => {
    open();

    if (!payload || !purchaseOrder.value) {
        return;
    }

    purchaseOrder.value.title = payload.title ?? '';
    purchaseOrder.value.supplier_id = payload.supplier ?? null;
    purchaseOrder.value.budget_entry_id = payload.entry;
    purchaseOrder.value.category_id = payload.category;

    applyDefaultTaxRate(payload.department ?? getCategoryDepartmentId(payload.category));
    await loadBudgetEntries();
    await populateLineItemFromBudgetEntry();
};

const toggleLineItemDiscount = (index: number): void => {
    if (!purchaseOrder.value?.line_items[index]) {
        return;
    }

    const lineItem = purchaseOrder.value.line_items[index];
    lineItem.discount_value = 0;

    if (lineItem.discount_type === 'none') {
        lineItem.discount_type = 'nominal';
    } else if (lineItem.discount_type === 'nominal') {
        lineItem.discount_type = 'percentage';
    } else {
        lineItem.discount_type = 'none';
    }

    debouncedUpdatePO();
};

const addWithholdingTaxLineItem = (): void => {
    if (!purchaseOrder.value) {
        return;
    }

    purchaseOrder.value.line_items.push({
        description: 'Withholding Tax',
        quantity: 1,
        is_withholding_tax: true,
        unit_price: -withholdingTaxInfo.amount,
        unit_price_target: -withholdingTaxInfo.amount,
        tax_rate_id: purchaseOrder.value.tax_rate_id ? String(purchaseOrder.value.tax_rate_id) : undefined,
    });
    addWithholdingTaxModal.value?.close();
    debouncedUpdatePO();
};

const updateSupplier = async (id: number | null) => {
    if (isLoading.value || isHydratingPurchaseOrder.value) {
        return;
    }

    if (!purchaseOrder.value) {
        return;
    }

    if (isApproved.value && id !== null) {
        if (initialSupplierId.value !== id) {
            const result = await confirm({
                title: 'Change Supplier',
                message:
                    'Changing the supplier will reset this purchase order to draft and remove any approvals. Are you sure you wish to continue?',
                confirmText: 'Change Supplier',
            });
            if (!result) {
                return;
            }
            purchaseOrder.value.supplier_id = id;
            initialSupplierId.value = id;
            // reset to draft
            purchaseOrder.value.status = 'Draft';
            await updatePO();
            capturePosthogEvent('purchase_order_reset_to_draft');
        }
    } else if (!isApproved.value && id !== null) {
        purchaseOrder.value.supplier_id = id;
        initialSupplierId.value = id;
        updatePO();
    }
};

const deletePO = () => {
    if (!purchaseOrder.value?.id) {
        return;
    }

    isUpdating.value = true;
    const urlStr = route('api.account.event.purchaseorders.delete', {
        account: purchaseOrder.value.account_id,
        event: purchaseOrder.value.event_id,
        purchaseOrder: purchaseOrder.value.id,
    });
    axios.delete(urlStr).then(() => {
        capturePosthogEvent('purchase_order_deleted');
        isUpdating.value = false;
        Eventbus.$emit('reload:purchaseorders');
        emitUntypedEvent('reload:statistics');
        close();
    });
};

const closePO = async () => {
    if (!purchaseOrder.value?.id) {
        return;
    }

    const result = await confirm({
        title: 'Close Purchase Order',
        message:
            'Closing this purchase order will adjust the total value to what has already been invoiced and prevent further editing. Are you sure you wish to continue?',
        confirmText: 'Close Purchase Order',
    });
    if (!result) {
        return;
    }
    isUpdating.value = true;
    try {
        const { data } = await axios.post(
            route('api.account.event.purchaseorders.close', {
                account: purchaseOrder.value.account_id,
                event: purchaseOrder.value.event_id,
                purchaseOrder: purchaseOrder.value.id,
            })
        );
        purchaseOrder.value = data;
        if (data.is_closed) {
            capturePosthogEvent('purchase_order_closed');
        }
    } catch (ex: unknown) {
        const error = ex as { response?: { data?: { message?: string } }; message?: string };

        toast(
            'Close Purchase Order',
            'An unexpected error occurred when attempting to close the purchase order: ' +
                (error.response?.data?.message ?? error.message),
            'error'
        );
    } finally {
        isUpdating.value = false;
    }
};

const reopenPO = async () => {
    if (!purchaseOrder.value?.id) {
        return;
    }

    isUpdating.value = true;
    try {
        const { data } = await axios.post(
            route('api.account.event.purchaseorders.close', {
                account: purchaseOrder.value.account_id,
                event: purchaseOrder.value.event_id,
                purchaseOrder: purchaseOrder.value.id,
            })
        );
        purchaseOrder.value = data;
    } catch (ex: unknown) {
        const error = ex as { response?: { data?: { message?: string } }; message?: string };

        toast(
            'Reopen Purchase Order',
            'An unexpected error occurred when attempting to reopen the purchase order: ' +
                (error.response?.data?.message ?? error.message),
            'error'
        );
    } finally {
        isUpdating.value = false;
    }
};

const downloadPDF = () => {
    if (!purchaseOrder.value?.id) {
        return;
    }

    capturePosthogEvent('purchase_order_downloaded');
    window.open(
        route('account.event.export.purchaseorders.pdf', {
            account: purchaseOrder.value.account_id,
            event: purchaseOrder.value.event_id,
            purchaseOrder: purchaseOrder.value.id,
        })
    );
};

const sendEmail = () => {
    if (!purchaseOrder.value?.id) {
        return;
    }

    emitUntypedEvent('send:email', {
        model: purchaseOrder.value,
        type: 'purchase order',
    });
};

const pushToAcumatica = async (): Promise<void> => {
    if (!purchaseOrder.value?.id || !purchaseOrder.value.account_id || !purchaseOrder.value.event_id) {
        return;
    }

    isPushingToAcumatica.value = true;

    try {
        const { data } = await axios.post(
            route('api.account.event.purchaseorders.push.acumatica', {
                account: purchaseOrder.value.account_id,
                event: purchaseOrder.value.event_id,
                purchaseOrder: purchaseOrder.value.id,
            })
        );
        const updatedPurchaseOrder = data as EditablePurchaseOrder;
        updatedPurchaseOrder.line_items.forEach(normaliseLineItemDiscount);
        purchaseOrder.value = updatedPurchaseOrder;
        toast('Success', 'Purchase Order pushed to Acumatica successfully', 'success');
    } catch (ex: unknown) {
        const error = ex as { response?: { data?: { message?: string } }; message?: string };

        toast(
            'Error',
            'An error occurred while pushing the Purchase Order to Acumatica: ' +
                (error.response?.data?.message ?? error.message),
            'error'
        );
    } finally {
        isPushingToAcumatica.value = false;
    }
};

const alterCurrency = (): void => {
    if (!purchaseOrder.value) {
        return;
    }

    (alterCurrencyModal.value as { open?: (...args: unknown[]) => void } | null)?.open?.(
        purchaseOrder.value.base_currency,
        purchaseOrder.value.target_currency,
        purchaseOrder.value.conversion_rate,
        purchaseOrder.value.total
    );
};

const updateCurrency = (payload: { source: string; target: string; rate: number | string }): void => {
    if (isLoading.value || isHydratingPurchaseOrder.value) {
        return;
    }

    if (!purchaseOrder.value) {
        return;
    }

    purchaseOrder.value.base_currency = payload.source;
    purchaseOrder.value.target_currency = payload.target;
    purchaseOrder.value.conversion_rate = payload.rate;
    updatePO();
};

const openAttachableDocuments = (): void => {
    if (!purchaseOrder.value) {
        return;
    }

    (attachDocumentsModal.value as { open?: (...args: unknown[]) => void } | null)?.open?.(
        purchaseOrder.value.file_uploads ?? []
    );
};

const saveAttachableDocuments = (selectedDocuments: FileUpload[]): void => {
    if (isLoading.value || isHydratingPurchaseOrder.value) {
        return;
    }

    if (!purchaseOrder.value) {
        return;
    }

    purchaseOrder.value.file_uploads = selectedDocuments;
    updatePO();
};

const viewInvoice = (supplierInvoiceId: number, openPaymentDates = false): void => {
    if (!purchaseOrder.value?.id) {
        return;
    }

    Eventbus.$emit('viewInvoice', {
        type: 'SupplierInvoice',
        routeData: {
            event: purchaseOrder.value.event_id,
            purchaseOrder: purchaseOrder.value.id,
            supplierInvoice: supplierInvoiceId,
            openPaymentDates,
        },
    });
};

const raiseInvoice = (): void => {
    if (!purchaseOrder.value?.id) {
        return;
    }
    Eventbus.$emit('purchaseInvoice:create', [purchaseOrder.value as unknown as Record<string, unknown>]);
};

const downloadAttachment = (fileUpload: FileUpload): void => {
    if (!fileUpload.download_url) {
        return;
    }

    window.open(fileUpload.download_url, '_blank');
};

const bytes2kb = (bytes: number | string): string => {
    return (parseFloat(String(bytes)) / 1000).toFixed(1);
};

const bytes2mb = (bytes: number | string): string => {
    return (parseFloat(bytes2kb(bytes)) / 1000).toFixed(1);
};

const modifiedInvoiceAmount = (modifier: number | string, amount: number | string | undefined): number => {
    return (parseFloat(String(modifier)) / 100) * toNumber(amount);
};

const isMultiPurchaseOrderInvoice = (invoice: SupplierInvoice): boolean => {
    return !!invoice.pivot && parseFloat(String(invoice.pivot.value_modifier)) < 100;
};

const invoiceHasAttachments = (invoice: SupplierInvoice): boolean => {
    return !!invoice.file_uploads?.length;
};

const invoiceRowClass = (invoice: SupplierInvoice): string => {
    return isMultiPurchaseOrderInvoice(invoice) ? 'tw-bg-orange-50' : '';
};

const invoiceHasCredit = (invoice: SupplierInvoice): boolean => {
    return !!toNumber(invoice.total_credit) || !!toNumber(invoice.total_credit_target);
};

const invoiceIsMultiCurrency = (invoice: SupplierInvoice): boolean => {
    return !!invoice.base_currency && !!invoice.target_currency && invoice.base_currency !== invoice.target_currency;
};

const invoiceSubtotal = (invoice: SupplierInvoice, target = true): number | string | undefined => {
    const subtotalKey = target && invoice.subtotal_target ? 'subtotal_target' : 'subtotal';

    if (isMultiPurchaseOrderInvoice(invoice)) {
        return modifiedInvoiceAmount(invoice.pivot?.value_modifier ?? 0, invoice[subtotalKey]);
    }

    return invoice[subtotalKey];
};

const invoiceTaxAmount = (invoice: SupplierInvoice, target = true): number | string | undefined => {
    const taxKey = target && invoice.tax_amount_target ? 'tax_amount_target' : 'tax_amount';

    if (isMultiPurchaseOrderInvoice(invoice)) {
        return modifiedInvoiceAmount(invoice.pivot?.value_modifier ?? 0, invoice[taxKey]);
    }

    return invoice[taxKey];
};

const invoiceTargetTotalValue = (invoice: SupplierInvoice): number | string | undefined => {
    if (hasValue(invoice.total_value_target)) {
        return invoice.total_value_target;
    }

    if (hasValue(invoice.total_target_value)) {
        return invoice.total_target_value;
    }

    if (hasValue(invoice.total_target)) {
        return invoice.total_target;
    }

    return invoice.total_value;
};

const invoiceCreditValue = (invoice: SupplierInvoice): number | string | undefined => {
    return hasValue(invoice.total_credit_target) ? invoice.total_credit_target : invoice.total_credit;
};

const invoiceTotalValue = (invoice: SupplierInvoice, target = true): number => {
    const totalValue = target ? invoiceTargetTotalValue(invoice) : invoice.total_value;
    const creditValue = target ? invoiceCreditValue(invoice) : invoice.total_credit;
    const value = toNumber(totalValue) - toNumber(creditValue);

    if (isMultiPurchaseOrderInvoice(invoice)) {
        return modifiedInvoiceAmount(invoice.pivot?.value_modifier ?? 0, value);
    }

    return value;
};

const openLinkedEntry = () => {
    if (!purchaseOrder.value?.id || !purchaseOrder.value.event_id || !purchaseOrder.value.budget_entry_id) {
        return;
    }

    emitUntypedEvent('budget:entry', {
        event: purchaseOrder.value.event_id,
        entry: purchaseOrder.value.budget_entry_id,
        source: {
            emitter: 'open:PurchaseOrder',
            payload: {
                event: purchaseOrder.value.event_id,
                entry: purchaseOrder.value.budget_entry_id,
            },
        },
    });
};

const onLineItemsReordered = async (reorderedItems: LineItem[]) => {
    if (!purchaseOrder.value?.id) {
        return;
    }
    purchaseOrder.value.line_items = reorderedItems;
    const ids = reorderedItems.filter((item) => item.id).map((item) => item.id);
    if (!ids.length) {
        return;
    }
    await axios.patch(
        route('api.account.event.purchaseorders.line-items.reorder', {
            account: purchaseOrder.value.account_id,
            event: purchaseOrder.value.event_id,
            purchaseOrder: purchaseOrder.value.id,
        }),
        { ids }
    );
    capturePosthogEvent('purchase_order_updated');
};

async function addComment(commentText: string): Promise<void> {
    if (!commentText || !purchaseOrder.value?.id || !purchaseOrder.value?.event_id) {
        toast('Warning', 'You must provide some text for your comment', 'warning');
        return;
    }

    isUpdating.value = true;

    try {
        const response = await axios.post(
            route('api.account.event.purchaseorders.comment', {
                event: purchaseOrder.value.event_id,
                purchaseOrder: purchaseOrder.value.id,
            }),
            { text: commentText }
        );

        purchaseOrder.value.comments = [...(purchaseOrder.value.comments ?? []), response.data.comment];
    } finally {
        isUpdating.value = false;
    }
}

async function handlePurchaseOrderView(payload: { purchaseOrder?: number }): Promise<void> {
    open();

    if (payload.purchaseOrder) {
        await loadPurchaseOrder(payload.purchaseOrder);
    }
}

async function handlePurchaseOrderCreate(payload: CreatePurchaseOrderEventPayload): Promise<void> {
    await openForCreation(payload || undefined);
}

async function handleSupplierInvoiceChanged(payload: { purchaseOrders?: number[] }): Promise<void> {
    if (!purchaseOrder.value?.id || !payload.purchaseOrders?.includes(purchaseOrder.value.id)) {
        return;
    }

    await loadPurchaseOrder(purchaseOrder.value.id);
}

onMounted(() => {
    Eventbus.$on('purchaseOrder:view', handlePurchaseOrderView);
    Eventbus.$on('purchase-order:create', handlePurchaseOrderCreate);
    Eventbus.$on('supplierInvoice:created', handleSupplierInvoiceChanged);
    Eventbus.$on('supplierInvoice:deleted', handleSupplierInvoiceChanged);
});

onUnmounted(() => {
    Eventbus.$off('purchaseOrder:view', handlePurchaseOrderView);
    Eventbus.$off('purchase-order:create', handlePurchaseOrderCreate);
    Eventbus.$off('supplierInvoice:created', handleSupplierInvoiceChanged);
    Eventbus.$off('supplierInvoice:deleted', handleSupplierInvoiceChanged);
});

defineExpose({
    open,
    close,
});
</script>

<style scoped lang="scss">
.activity-heading-enter-active,
.activity-heading-leave-active,
.activity-content-enter-active,
.activity-content-leave-active {
    transition:
        opacity 160ms ease,
        transform 180ms ease;
}

.activity-heading-enter-from,
.activity-heading-leave-to,
.activity-content-enter-from,
.activity-content-leave-to {
    opacity: 0;
    transform: translateX(8px);
}
</style>
